/**
 * KioskPage — 키오스크 관리
 *
 * 탭 1. 코스 관리  — 코스 추가 / 수정 / 비활성화
 * 탭 2. 결제 현황  — 일별 요약 + 결제 로그
 * 탭 3. 점검 모드  — 세차기별 점검 ON/OFF WebSocket push
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Paper, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Button, TextField, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Switch, FormControlLabel, Select, MenuItem,
  InputLabel, FormControl, CircularProgress, Card, CardContent,
  Divider, Alert, Snackbar, Stack, useTheme, useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BuildIcon from '@mui/icons-material/Build';
import api from '../api/client';

const WASHERS = ['세차기1', '세차기2', '세차기3'];
const today = () => new Date().toLocaleDateString('sv-SE'); // YYYY-MM-DD

// ── 탭 패널 ──────────────────────────────────────────────────────
function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ══════════════════════════════════════════════════════════════════
// 탭 1: 코스 관리
// ══════════════════════════════════════════════════════════════════
function CourseTab({ onToast }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // null = 신규, object = 수정
  const [form, setForm] = useState({ courseId: '', name: '', tag: '', basePrice: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/kiosk/admin/courses');
      setCourses(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditTarget(null);
    setForm({ courseId: '', name: '', tag: '', basePrice: '' });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditTarget(c);
    setForm({ courseId: c.courseId, name: c.name, tag: c.tag, basePrice: String(c.basePrice) });
    setDialogOpen(true);
  };

  const save = async () => {
    const body = { ...form, basePrice: Number(form.basePrice), active: true };
    try {
      if (editTarget) {
        await api.patch(`/kiosk/admin/courses/${editTarget._id}`, body);
        onToast('코스가 수정되었습니다.');
      } else {
        await api.post('/kiosk/admin/courses', body);
        onToast('코스가 추가되었습니다.');
      }
      setDialogOpen(false);
      load();
    } catch {
      onToast('저장 실패', 'error');
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm('비활성화하면 키오스크에 표시되지 않습니다.')) return;
    try {
      await api.delete(`/kiosk/admin/courses/${id}`);
      onToast('비활성화 완료');
      load();
    } catch {
      onToast('실패', 'error');
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
          코스 추가
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : isMobile ? (
        /* ── 모바일: 카드 리스트 ── */
        <Stack spacing={1.25}>
          {courses.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: '#aaa' }}>코스 없음</Paper>
          ) : (
            courses.map((c) => (
              <Paper key={c._id} elevation={0} sx={{ p: 1.75, border: '1px solid #e0e0e0', borderRadius: '12px' }}>
                {/* 상단: 코스명 + 상태 */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography fontWeight={700} fontSize={15} sx={{ wordBreak: 'keep-all' }}>{c.name}</Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>{c.courseId}</Typography>
                  </Box>
                  <Chip
                    label={c.active ? '운영중' : '비활성'}
                    size="small"
                    color={c.active ? 'success' : 'default'}
                  />
                </Box>

                {/* 중간: 태그 + 가격 */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1 }}>
                  <Typography variant="body2" sx={{ color: '#555', wordBreak: 'keep-all' }}>{c.tag}</Typography>
                  <Typography fontWeight={700}>{c.basePrice.toLocaleString()}원</Typography>
                </Box>

                {/* 하단: 액션 버튼 */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon fontSize="small" />}
                    onClick={() => openEdit(c)}
                    sx={{ flex: 1 }}
                  >
                    수정
                  </Button>
                  {c.active && (
                    <Button size="small" color="error" variant="outlined" onClick={() => deactivate(c._id)} sx={{ flex: 1 }}>
                      비활성화
                    </Button>
                  )}
                </Box>
              </Paper>
            ))
          )}
        </Stack>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small" sx={{ "& td[align=\"center\"], & th[align=\"center\"]": { whiteSpace: "nowrap" } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {['코스 ID', '코스명', '태그', '기본가격', '상태', ''].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {courses.map((c) => (
                <TableRow key={c._id} hover>
                  <TableCell sx={{ color: '#888', fontSize: 12 }}>{c.courseId}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{c.name}</TableCell>
                  <TableCell>{c.tag}</TableCell>
                  <TableCell>{c.basePrice.toLocaleString()}원</TableCell>
                  <TableCell>
                    <Chip
                      label={c.active ? '운영중' : '비활성'}
                      size="small"
                      color={c.active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton>
                    {c.active && (
                      <Button size="small" color="error" onClick={() => deactivate(c._id)} sx={{ ml: 0.5 }}>
                        비활성화
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {courses.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#aaa' }}>코스 없음</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 추가/수정 다이얼로그 */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editTarget ? '코스 수정' : '코스 추가'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="코스 ID" value={form.courseId} onChange={(e) => setForm({ ...form, courseId: e.target.value })}
            disabled={!!editTarget} size="small" placeholder="all_in_one" />
          <TextField label="코스명" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} size="small" />
          <TextField label="태그" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} size="small" placeholder="드라잉존 무료" />
          <TextField label="기본가격 (원)" type="number" value={form.basePrice}
            onChange={(e) => setForm({ ...form, basePrice: e.target.value })} size="small" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button variant="contained" onClick={save}
            disabled={!form.courseId || !form.name || !form.basePrice}>저장</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════
// 탭 2: 결제 현황
// ══════════════════════════════════════════════════════════════════
function PaymentTab() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [date, setDate]       = useState(today());
  const [washerId, setWasherId] = useState('');
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ date, ...(washerId && { washerId }) });
      const [sumRes, payRes] = await Promise.all([
        api.get(`/kiosk/admin/payments/summary?date=${date}`),
        api.get(`/kiosk/admin/payments?${qs}`),
      ]);
      setSummary(sumRes.data);
      setPayments(payRes.data.payments);
    } finally {
      setLoading(false);
    }
  }, [date, washerId]);

  useEffect(() => { load(); }, [load]);

  const STATUS_COLOR = { success: 'success', failed: 'error', cancelled: 'default' };
  const STATUS_LABEL = { success: '성공', failed: '실패', cancelled: '취소' };

  return (
    <>
      {/* 필터 */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
        <TextField type="date" label="날짜" size="small" value={date}
          onChange={(e) => setDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>세차기</InputLabel>
          <Select value={washerId} onChange={(e) => setWasherId(e.target.value)} label="세차기">
            <MenuItem value="">전체</MenuItem>
            {WASHERS.map((w) => <MenuItem key={w} value={w}>{w}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {/* 요약 카드 */}
      {summary && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {[
            { label: '결제 건수', value: `${summary.count}건` },
            { label: '총 결제액', value: `${summary.totalAmount.toLocaleString()}원` },
          ].map(({ label, value }) => (
            <Card key={label} elevation={1} sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color: '#E91E63', mt: 0.5 }}>{value}</Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* 결제 테이블 */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : isMobile ? (
        /* ── 모바일: 카드 리스트 ── */
        <Stack spacing={1.25}>
          {payments.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: '#aaa' }}>결제 내역 없음</Paper>
          ) : (
            payments.map((p) => (
              <Paper key={p._id} elevation={0} sx={{ p: 1.75, border: '1px solid #e0e0e0', borderRadius: '12px' }}>
                {/* 상단: 시각 + 상태 */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" sx={{ color: '#555' }}>
                    {new Date(p.createdAt).toLocaleTimeString('ko-KR')}
                  </Typography>
                  <Chip
                    label={STATUS_LABEL[p.status] ?? p.status}
                    size="small"
                    color={STATUS_COLOR[p.status] ?? 'default'}
                  />
                </Box>

                {/* 중간: 코스명 (강조) + 세차기 칩 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Typography fontWeight={700} fontSize={15} sx={{ wordBreak: 'keep-all' }}>
                    {p.courseName}
                  </Typography>
                  <Chip label={p.washerId} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                </Box>

                {/* 하단: 결제방식 / 하부세차 / 금액 */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 0.75 }}>
                    <Chip
                      label={p.paymentMethod === 'card' ? '카드' : 'RF'}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                    {p.hasUnderWash && (
                      <Chip label="하부세차" size="small" color="info" sx={{ fontSize: 11 }} />
                    )}
                  </Box>
                  <Typography fontWeight={700}>
                    {p.amount.toLocaleString()}원
                  </Typography>
                </Box>
              </Paper>
            ))
          )}
        </Stack>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small" sx={{ "& td[align=\"center\"], & th[align=\"center\"]": { whiteSpace: "nowrap" } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {['시각', '세차기', '코스', '하부세차', '결제방식', '금액', '상태'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p._id} hover>
                  <TableCell sx={{ fontSize: 12, color: '#555' }}>
                    {new Date(p.createdAt).toLocaleTimeString('ko-KR')}
                  </TableCell>
                  <TableCell>{p.washerId}</TableCell>
                  <TableCell>{p.courseName}</TableCell>
                  <TableCell>{p.hasUnderWash ? '✓' : '–'}</TableCell>
                  <TableCell>{p.paymentMethod === 'card' ? '카드' : 'RF'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{p.amount.toLocaleString()}원</TableCell>
                  <TableCell>
                    <Chip label={STATUS_LABEL[p.status] ?? p.status}
                      size="small" color={STATUS_COLOR[p.status] ?? 'default'} />
                  </TableCell>
                </TableRow>
              ))}
              {payments.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#aaa' }}>결제 내역 없음</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════
// 탭 3: 점검 모드
// ══════════════════════════════════════════════════════════════════
function MaintenanceTab({ onToast }) {
  const [states, setStates] = useState(
    Object.fromEntries(WASHERS.map((w) => [w, false]))
  );
  const [loading, setLoading] = useState({});
  // 토글 즉시 API 호출하면 실수로 한 번 잘못 클릭 시 영업 중단.
  // 확인 다이얼로그를 거쳐 사유와 함께 진행하도록 변경.
  const [confirmDialog, setConfirmDialog] = useState({ open: false, washerId: null, nextState: null });
  const [reason, setReason] = useState('');

  const requestToggle = (washerId) => {
    const next = !states[washerId];
    setConfirmDialog({ open: true, washerId, nextState: next });
    setReason('');
  };

  const closeConfirm = () => {
    if (loading[confirmDialog.washerId]) return;
    setConfirmDialog({ open: false, washerId: null, nextState: null });
    setReason('');
  };

  const performToggle = async () => {
    const { washerId, nextState } = confirmDialog;
    if (!washerId) return;
    setLoading((p) => ({ ...p, [washerId]: true }));
    try {
      // reason은 백엔드 maintenance 엔드포인트가 받지 않더라도 ValidationPipe whitelist를
      // 통과시키기 위해 보내지 않음. 추후 백엔드 KioskMaintenanceLog 추가 시 함께 전달.
      await api.post('/kiosk/maintenance', { washerId, active: nextState });
      setStates((p) => ({ ...p, [washerId]: nextState }));
      onToast(`${washerId} 점검 모드 ${nextState ? 'ON' : 'OFF'}${reason ? ` (${reason})` : ''}`);
      setConfirmDialog({ open: false, washerId: null, nextState: null });
      setReason('');
    } catch {
      onToast('전송 실패', 'error');
    } finally {
      setLoading((p) => ({ ...p, [washerId]: false }));
    }
  };

  return (
    <Box sx={{ maxWidth: 480 }}>
      <Alert severity="info" sx={{ mb: 3 }}>
        점검 모드를 켜면 해당 세차기 키오스크 화면이 "점검 중" 화면으로 전환됩니다.
        영업에 직접 영향을 주므로 확인 후 진행됩니다.
      </Alert>
      <Paper elevation={1} sx={{ divide: 'y' }}>
        {WASHERS.map((washerId, i) => (
          <Box key={washerId}>
            {i > 0 && <Divider />}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <BuildIcon sx={{ color: states[washerId] ? '#E91E63' : '#aaa' }} />
                <Box>
                  <Typography fontWeight={700}>{washerId}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {states[washerId] ? '점검 중' : '정상 운영'}
                  </Typography>
                </Box>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={states[washerId]}
                    onChange={() => requestToggle(washerId)}
                    disabled={loading[washerId]}
                    color="error"
                  />
                }
                label={states[washerId] ? 'ON' : 'OFF'}
                labelPlacement="start"
              />
            </Box>
          </Box>
        ))}
      </Paper>

      {/* 점검 모드 확인 다이얼로그 — 실수 방지 */}
      <Dialog open={confirmDialog.open} onClose={closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {confirmDialog.washerId} 점검 모드 {confirmDialog.nextState ? 'ON' : 'OFF'}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity={confirmDialog.nextState ? 'warning' : 'success'} sx={{ mb: 2 }}>
            {confirmDialog.nextState
              ? '키오스크가 "점검 중" 화면으로 즉시 전환되어 신규 결제·인증이 차단됩니다.'
              : '키오스크가 정상 운영 화면으로 복귀하여 결제·인증을 다시 받습니다.'}
          </Alert>
          <TextField
            label="사유 (선택)"
            fullWidth
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={confirmDialog.nextState ? '예) 릴레이 점검, 카드 단말 교체' : '예) 점검 완료, 정상 복귀'}
            slotProps={{ htmlInput: { maxLength: 200 } }}
            helperText={`${reason.length}/200`}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeConfirm} disabled={loading[confirmDialog.washerId]}>취소</Button>
          <Button
            onClick={performToggle}
            variant="contained"
            color={confirmDialog.nextState ? 'error' : 'success'}
            disabled={loading[confirmDialog.washerId]}
          >
            {loading[confirmDialog.washerId] ? '전송 중...' : `${confirmDialog.nextState ? '점검 시작' : '운영 재개'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════
// 메인 페이지
// ══════════════════════════════════════════════════════════════════
export default function KioskPage() {
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });

  const onToast = (msg, severity = 'success') =>
    setToast({ open: true, msg, severity });

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        키오스크 관리
      </Typography>

      <Paper elevation={0} sx={{ borderBottom: '1px solid #e0e0e0' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="코스 관리" />
          <Tab label="결제 현황" />
          <Tab label="점검 모드" />
        </Tabs>
      </Paper>

      <TabPanel value={tab} index={0}>
        <CourseTab onToast={onToast} />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <PaymentTab />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <MaintenanceTab onToast={onToast} />
      </TabPanel>

      <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast((p) => ({ ...p, open: false }))}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
